import React, { useState, useMemo } from "react";
import T from '../../components/common/T';
import EmployeeListView from "./EmployeeListView";
import EmployeeKanbanView from "./EmployeeKanbanView";
import EmployeeForm from "./EmployeeForm";
import { useWorkforce, WorkforceProvider } from "../../context/WorkforceContext";
import { useAuth } from "../../context/AuthContext";
import PageLayout, { PageHeader, StatsGrid, Toolbar } from "../../components/common/PageLayout";
import { TopPrimaryButton } from "../../components/common/StandardButtons";
import ViewSwitcher from "../../components/common/ViewSwitcher";
import {
  UserGroupIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { 
  X, Mail, Phone, MapPin, Edit 
} from "lucide-react";

const EmployeePageInner = () => {
  const { workforce, fetchWorkforce, loading } = useWorkforce(); 
  const { checkAccess } = useAuth(); 

  React.useEffect(() => {
    if (fetchWorkforce) fetchWorkforce();
  }, [fetchWorkforce]);

  const [view, setView] = useState("list");
  const [showForm, setShowForm] = useState(false);
  const [employeeToEdit, setEmployeeToEdit] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");

  const handleEdit = (employee) => { setEmployeeToEdit(employee); setShowForm(true); setSelectedEmployee(null); };
  const handleAddNew = () => { setEmployeeToEdit(null); setShowForm(true); setSelectedEmployee(null); };
  const handleCloseForm = () => { setShowForm(false); setEmployeeToEdit(null); };

  // 🟢 DYNAMIC HR KPIs
  const statsCounts = useMemo(() => {
    const list = Array.isArray(workforce) ? workforce : [];
    return {
      total: list.length,
      active: list.filter(e => e.status === 'Active' || !e.status).length,
      onLeave: list.filter(e => e.status === 'On Leave').length,
      inactive: list.filter(e => e.status === 'Inactive' || e.status === 'Terminated').length
    };
  }, [workforce]);

  const stats = useMemo(() => [
    {
      label: "Total Staff",
      value: statsCounts.total,
      icon: UserGroupIcon,
      variant: "default",
      isActive: filter === "All" || !filter,
      onClick: () => setFilter("All"),
    },
    {
      label: "Active",
      value: statsCounts.active,
      icon: CheckCircleIcon,
      variant: "green",
      isActive: filter === "Active",
      onClick: () => setFilter(f => f === "Active" ? "All" : "Active"),
    },
    {
      label: "On Leave",
      value: statsCounts.onLeave,
      icon: ClockIcon,
      variant: "orange",
      isActive: filter === "On Leave",
      onClick: () => setFilter(f => f === "On Leave" ? "All" : "On Leave"),
    },
    {
      label: "Inactive",
      value: statsCounts.inactive,
      icon: XCircleIcon,
      variant: "red",
      isActive: filter === "Inactive",
      onClick: () => setFilter(f => f === "Inactive" ? "All" : "Inactive"),
    },
  ], [statsCounts, filter]);

  return (
    <PageLayout>
      {/* 1. HEADER */}
      <PageHeader
        icon={UserGroupIcon}
        iconColor="text-[#E8612C]"
        title="Workforce Directory"
        subtitle="Manage employee profiles, roles, and status"
        onRefresh={() => fetchWorkforce && fetchWorkforce()}
        loading={loading}
      >
        {showForm ? (
          <button
            onClick={handleCloseForm}
            className="h-8 px-3 rounded-lg bg-gray-200 dark:bg-[#30363d] text-gray-800 dark:text-white text-xs font-bold hover:bg-gray-300 dark:hover:bg-slate-700 transition"
          >
            ← Back to List
          </button>
        ) : (
          checkAccess('workforce.create') && (
            <TopPrimaryButton
              onClick={handleAddNew}
              label="Onboard Employee"
            />
          )
        )}
      </PageHeader>

      {showForm ? (
        <EmployeeForm onClose={handleCloseForm} employeeToEdit={employeeToEdit} />
      ) : (
        <>
          {/* 2. KPI DASHBOARD */}
          <StatsGrid stats={stats} cols={4} />

          {/* 3. CONTROL BAR */}
          <Toolbar
            searchTerm={search}
            onSearch={setSearch}
            searchPlaceholder="Search by name, role, or email..."
            tabs={['All', 'Active', 'On Leave', 'Inactive']}
            activeTab={filter}
            onTabChange={setFilter}
          >
            <ViewSwitcher
              view={view}
              onViewChange={setView}
              availableViews={['list', 'kanban']}
            />
          </Toolbar>

          {/* 4. SPLIT PANE LAYOUT */}
          <div className="flex flex-col lg:flex-row gap-4 flex-1 min-h-0 overflow-hidden">
            
            {/* Left: List/Kanban */}
            <div className={`flex-1 bg-white dark:bg-[#161b22] rounded-xl shadow-sm border border-gray-200 dark:border-[#30363d] overflow-hidden flex flex-col min-h-0 transition-all duration-300 ${selectedEmployee ? 'hidden lg:flex' : 'flex'}`}>
              {loading ? (
                <div className="p-8 text-center text-gray-500 dark:text-slate-400 text-xs"><T>Loading workforce data...</T></div>
              ) : view === "list" ? (
                <EmployeeListView onEdit={handleEdit} onSelect={setSelectedEmployee} filter={filter} search={search} />
              ) : (
                <EmployeeKanbanView onEdit={handleEdit} onSelect={setSelectedEmployee} filter={filter} search={search} />
              )}
            </div>

            {/* Right: Slide-in Detail Panel */}
            {selectedEmployee && (
                <div className="w-full lg:w-[400px] xl:w-[450px] bg-white dark:bg-[#161b22] rounded-xl shadow-lg border border-gray-200 dark:border-[#30363d] flex flex-col overflow-hidden animate-slide-in-right shrink-0 z-10 h-[calc(100vh-250px)]">
                    
                    {/* Panel Header */}
                    <div className="bg-gray-50 dark:bg-[#21262d] px-4 py-3 border-b border-gray-200 dark:border-[#30363d] flex justify-between items-center shrink-0">
                        <h3 className="font-bold text-gray-800 dark:text-white text-sm"><T>Employee Profile</T></h3>
                        <button onClick={() => setSelectedEmployee(null)} className="text-gray-400 dark:text-slate-500 hover:text-gray-800 dark:hover:text-white transition"><X size={16} /></button>
                    </div>

                    {/* Panel Content */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                        <div className="text-center pb-4 border-b border-gray-100 dark:border-[#30363d]">
                            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-2">
                                {selectedEmployee.employeeName?.charAt(0) || 'U'}
                            </div>
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white">{selectedEmployee.employeeName}</h2>
                            <p className="text-xs text-gray-500 dark:text-slate-400 font-medium">{selectedEmployee.designation || 'No Role Assigned'}</p>
                            <div className="mt-2">
                                <span className={`px-2 py-0.5 rounded-[4px] text-[10px] font-bold uppercase border ${selectedEmployee.status === 'Active' ? 'bg-green-100 dark:bg-green-950/40 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800' : selectedEmployee.status === 'On Leave' ? 'bg-orange-100 dark:bg-orange-950/40 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800' : 'bg-red-100 dark:bg-red-950/40 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800'}`}>
                                    {selectedEmployee.status || 'Active'}
                                </span>
                            </div>
                        </div>

                        <div className="space-y-2.5">
                            <h4 className="text-[10px] font-bold uppercase text-gray-400 dark:text-slate-500 tracking-wider mb-1"><T>Contact Info</T></h4>
                            <div className="flex items-center gap-2 text-xs text-gray-700 dark:text-slate-300 bg-gray-50 dark:bg-[#21262d] p-2 rounded border border-gray-100 dark:border-[#30363d]"><Mail size={14} className="text-gray-400 dark:text-slate-500 shrink-0"/> <span className="truncate">{selectedEmployee.email || '-'}</span></div>
                            <div className="flex items-center gap-2 text-xs text-gray-700 dark:text-slate-300 bg-gray-50 dark:bg-[#21262d] p-2 rounded border border-gray-100 dark:border-[#30363d]"><Phone size={14} className="text-gray-400 dark:text-slate-500 shrink-0"/> <span>{selectedEmployee.phone || '-'}</span></div>
                            <div className="flex items-center gap-2 text-xs text-gray-700 dark:text-slate-300 bg-gray-50 dark:bg-[#21262d] p-2 rounded border border-gray-100 dark:border-[#30363d]"><MapPin size={14} className="text-gray-400 dark:text-slate-500 shrink-0"/> <span>{selectedEmployee.address || 'No address on file'}</span></div>
                        </div>

                        <div className="space-y-2.5">
                            <h4 className="text-[10px] font-bold uppercase text-gray-400 dark:text-slate-500 tracking-wider mb-1"><T>Work Details</T></h4>
                            <div className="grid grid-cols-2 gap-2">
                                <div className="bg-white dark:bg-[#21262d] border border-gray-200 dark:border-[#30363d] p-2 rounded">
                                    <p className="text-[9px] text-gray-400 dark:text-slate-500 font-bold uppercase mb-0.5"><T>Department</T></p>
                                    <p className="text-xs font-bold text-gray-800 dark:text-white truncate">{selectedEmployee.department || '-'}</p>
                                </div>
                                <div className="bg-white dark:bg-[#21262d] border border-gray-200 dark:border-[#30363d] p-2 rounded">
                                    <p className="text-[9px] text-gray-400 dark:text-slate-500 font-bold uppercase mb-0.5"><T>Join Date</T></p>
                                    <p className="text-xs font-bold text-gray-800 dark:text-white">{selectedEmployee.joinDate ? new Date(selectedEmployee.joinDate).toLocaleDateString() : '-'}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Panel Footer */}
                    <div className="bg-gray-50 dark:bg-[#21262d] px-4 py-3 border-t border-gray-200 dark:border-[#30363d] flex justify-end gap-2 shrink-0">
                        <button onClick={() => setSelectedEmployee(null)} className="px-4 py-1.5 bg-white dark:bg-[#161b22] border border-gray-300 dark:border-[#30363d] text-gray-700 dark:text-slate-300 rounded text-xs font-bold hover:bg-gray-100 dark:hover:bg-slate-800 transition">Close</button>
                        {checkAccess('workforce.edit') && (
                            <button onClick={() => handleEdit(selectedEmployee)} className="px-4 py-1.5 bg-blue-600 text-white rounded text-xs font-bold hover:bg-blue-700 transition flex items-center gap-1 shadow-sm"><Edit size={12}/> Edit Profile</button>
                        )}
                    </div>
                </div>
            )}
          </div>
        </>
      )}
    </PageLayout>
  );
};

const EmployeePage = () => (
  <WorkforceProvider>
    <EmployeePageInner />
  </WorkforceProvider>
);

export default EmployeePage;